import { useQConfig } from '@exam-management/feature-qconfig-provider';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { Button, ConfirmationModal, PublicIcon, Text, type ButtonProps } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useLogger } from '@shared/logger';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

export default function PublishQConfigButton(props: Readonly<ButtonProps>) {
  const [isPublishConfirmModalOpen, publishConfirmModalHandler] = useDisclosure();
  const { formMethodsRef, publishQConfig } = useQConfig();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const hasUpdateQConfigPermission = useHasPermission(APP_PERMISSIONS.EXAM.QCONFIG.UPDATE);
  const logger = useLogger();

  const handleInvalidForm = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleMutatePublishQConfig = async () => {
    try {
      await publishQConfig.execute({
        onCompleted: () => {
          showSnackbar({
            message: 'QConfig has been published',
            variant: 'success',
          });
        },
        onError: () => {
          showSnackbar({
            message: 'Unable to publish QConfig',
            variant: 'error',
          });
        },
      });
    } catch (error) {
      logger.error('Failed to publish QConfig', { error });
      showSnackbar({
        message: 'Failed to publish QConfig',
        variant: 'error',
      });
    }
  };

  const handleConfirmPublish = async () => {
    setIsLoading(true);

    try {
      if (!hasUpdateQConfigPermission) {
        await handleMutatePublishQConfig();
      } else {
        const { formContext, onSubmit } = formMethodsRef?.current ?? {};
        if (formContext && onSubmit) {
          await formContext.handleSubmit(async (data) => {
            await onSubmit(data);
            await handleMutatePublishQConfig();
          }, handleInvalidForm)();
        } else {
          showSnackbar({
            message: 'Form is not ready',
            variant: 'error',
          });
        }
      }
    } catch (error) {
      logger.error('Failed to save and publish QConfig', { error });
      showSnackbar({
        message: 'Failed to save and publish QConfig',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
      publishConfirmModalHandler.close();
    }
  };

  return (
    <>
      <Button
        startIcon={<PublicIcon />}
        onClick={publishConfirmModalHandler.open}
        loading={isLoading}
        {...props}
      >
        Publish
      </Button>
      <ConfirmationModal
        title='Publish QConfig?'
        content={
          <>
            เมื่อทำการ Publish แล้ว{' '}
            <Text component='span' variant='body1Semibold'>
              จะไม่สามารถแก้ไขโครงสร้างข้อสอบได้อีก
            </Text>
            <br />
            กรุณาตรวจสอบความถูกต้องให้เรียบร้อยก่อนดำเนินการต่อ
          </>
        }
        acceptText='Publish'
        open={isPublishConfirmModalOpen}
        onAccept={handleConfirmPublish}
        onDiscard={publishConfirmModalHandler.close}
        onClose={publishConfirmModalHandler.close}
        acceptButtonProps={{
          sx: {
            minWidth: '100px',
          },
        }}
      />
    </>
  );
}
